import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Receipt, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { DocumentFlowButton } from "@/components/DocumentFlowDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const CAN_MANAGE = ["ORG_ADMIN", "ACCOUNTS"];

export default function RABills() {
  const { user } = useAuth();
  const canManage = CAN_MANAGE.includes(user?.role);
  const { companies } = useCompany() || {};
  const [bills, setBills] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [billRes, projRes, clientRes] = await Promise.all([
        api.get("/revenue/ra-bills"), api.get("/projects"), api.get("/revenue/clients"),
      ]);
      setBills(billRes.data);
      setProjects(projRes.data);
      setClients(clientRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";
  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  const post = async (bill) => {
    setBusyId(bill.id);
    try {
      await api.post(`/revenue/ra-bills/${bill.id}/post`);
      toast.success("RA bill posted to GL (Sales Revenue)");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (bill) => {
    if (!window.confirm(`Delete draft RA bill ${bill.number}? Nothing has been posted, so nothing is reversed.`)) return;
    setBusyId(bill.id);
    try {
      await api.delete(`/revenue/ra-bills/${bill.id}`);
      toast.success("Draft RA bill deleted");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="ra-bills-page">
      <PageHeader title="RA Bills" description="Running account bills raised against project progress. Posting nets off retention & TDS receivable." action={canManage && <CreateRABillDialog companies={companies} projects={projects} clients={clients} onCreated={load} />} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && bills.length === 0 ? (
          <EmptyState title="No RA bills yet" testId="ra-bills-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Project</TableHead><TableHead>Client</TableHead><TableHead>Date</TableHead><TableHead className="text-right">% Complete</TableHead><TableHead className="text-right">Net receivable</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {bills.map((bill) => (
                <TableRow key={bill.id} data-testid={`ra-bill-row-${bill.number}`}>
                  <TableCell className="font-mono text-xs text-foreground">{bill.number}</TableCell>
                  <TableCell className="text-foreground">{projectName(bill.project_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{clientName(bill.client_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{bill.bill_date}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">{(Number(bill.cumulative_pct_complete) * 100).toFixed(1)}%</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{bill.net_receivable}</TableCell>
                  <TableCell><StatusPill value={bill.status} /></TableCell>
                  <TableCell className="text-right">
                    {canManage && bill.status === "DRAFT" && (
                      <div className="inline-flex gap-1.5">
                        <EditRABillDialog bill={bill} onSaved={load} />
                        <Button size="sm" variant="outline" disabled={busyId === bill.id} onClick={() => remove(bill)} title="Delete draft" data-testid={`delete-ra-bill-button-${bill.number}`}>
                          <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                        </Button>
                        <Button size="sm" variant="outline" disabled={busyId === bill.id} onClick={() => post(bill)} data-testid={`post-ra-bill-button-${bill.number}`}>
                          <Receipt className="h-3.5 w-3.5" /> Post
                        </Button>
                      </div>
                    )}
                    <DocumentFlowButton docType="RA_BILL" docId={bill.id} cancelEndpoint={bill.status === "POSTED" ? `/revenue/ra-bills/${bill.id}/cancel` : undefined} onCancelled={load} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateRABillDialog({ companies, projects, clients, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", project_id: "", client_id: "", bill_date: "", gross_amount: "", tax_pct: "0", retention_pct: "", tds_pct: "" });
  const [suggestion, setSuggestion] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  useEffect(() => {
    if (!form.project_id) { setSuggestion(null); return; }
    api.get(`/revenue/projects/${form.project_id}/ra-bill-suggestion`)
      .then(({ data }) => { setSuggestion(data); setForm((f) => ({ ...f, gross_amount: data.suggested_gross_amount })); })
      .catch((err) => toast.error(getErrorMessage(err)));
  }, [form.project_id]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, retention_pct: form.retention_pct || null, tds_pct: form.tds_pct || null, gross_amount: form.gross_amount || null };
      await api.post("/revenue/ra-bills", payload);
      toast.success("RA bill created");
      setForm({ company_id: "", project_id: "", client_id: "", bill_date: "", gross_amount: "", tax_pct: "0", retention_pct: "", tds_pct: "" });
      setSuggestion(null);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-ra-bill-button"><Plus className="h-4 w-4" /> New RA Bill</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New RA Bill</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-ra-bill-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Company</Label>
              <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="ra-bill-company-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Project</Label>
              <Select required value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="ra-bill-project-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Client</Label>
              <Select required value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="ra-bill-client-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(clients || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Bill date</Label><Input required type="date" data-testid="ra-bill-date-input" value={form.bill_date} onChange={set("bill_date")} className="mt-1.5" /></div>
          </div>
          {suggestion && (
            <p className="rounded-lg bg-muted/50 p-2.5 text-xs text-muted-foreground" data-testid="ra-bill-suggestion-note">
              Cumulative {(Number(suggestion.cumulative_pct_complete) * 100).toFixed(1)}% complete x contract value {suggestion.contract_value} = {suggestion.cumulative_eligible} eligible,
              minus {suggestion.previously_billed} already billed = suggested gross <span className="font-semibold text-foreground">{suggestion.suggested_gross_amount}</span>.
            </p>
          )}
          <div>
            <Label>Gross amount (auto-suggested, editable)</Label>
            <Input type="number" step="0.01" data-testid="ra-bill-gross-amount-input" value={form.gross_amount} onChange={set("gross_amount")} className="mt-1.5" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Retention % <span className="text-muted-foreground">(blank = client default)</span></Label><Input type="number" step="0.01" data-testid="ra-bill-retention-input" value={form.retention_pct} onChange={set("retention_pct")} className="mt-1.5" /></div>
            <div><Label>TDS % <span className="text-muted-foreground">(blank = client default)</span></Label><Input type="number" step="0.01" data-testid="ra-bill-tds-input" value={form.tds_pct} onChange={set("tds_pct")} className="mt-1.5" /></div>
            <div><Label>Tax %</Label><Input type="number" step="0.01" data-testid="ra-bill-tax-input" value={form.tax_pct} onChange={set("tax_pct")} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-ra-bill-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditRABillDialog({ bill, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  useEffect(() => {
    if (!open) return;
    setForm({
      bill_date: bill.bill_date, gross_amount: String(bill.gross_amount), tax_pct: String(bill.tax_pct),
      retention_pct: String(bill.retention_pct), tds_pct: String(bill.tds_pct), remarks: bill.remarks || "",
    });
  }, [open, bill]);

  const gross = Number(form.gross_amount || 0);
  const tax = (gross * Number(form.tax_pct || 0)) / 100;
  const net = gross + tax - (gross * Number(form.retention_pct || 0)) / 100 - (gross * Number(form.tds_pct || 0)) / 100;
  const inr = (n) => `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/revenue/ra-bills/${bill.id}`, { ...form, remarks: form.remarks || null });
      toast.success("RA bill updated");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" title="Edit draft" data-testid={`edit-ra-bill-button-${bill.number}`}><Pencil className="h-3.5 w-3.5" /></Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Edit {bill.number}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Bill date</Label><Input required type="date" value={form.bill_date || ""} onChange={set("bill_date")} className="mt-1.5" /></div>
            <div><Label>Gross amount (₹)</Label><Input required type="number" step="0.01" min="0" value={form.gross_amount ?? ""} onChange={set("gross_amount")} className="mt-1.5" data-testid="edit-ra-bill-gross" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>GST %</Label><Input type="number" step="0.01" min="0" max="100" value={form.tax_pct ?? ""} onChange={set("tax_pct")} className="mt-1.5" /></div>
            <div><Label>Retention %</Label><Input type="number" step="0.01" min="0" max="100" value={form.retention_pct ?? ""} onChange={set("retention_pct")} className="mt-1.5" /></div>
            <div><Label>TDS %</Label><Input type="number" step="0.01" min="0" max="100" value={form.tds_pct ?? ""} onChange={set("tds_pct")} className="mt-1.5" /></div>
          </div>
          <div><Label>Remarks</Label><Input value={form.remarks || ""} onChange={set("remarks")} className="mt-1.5" /></div>
          <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
            Progress at draft: {(Number(bill.cumulative_pct_complete) * 100).toFixed(2)}% · previously billed {inr(Number(bill.previously_billed))}
            <div className="mt-1 text-sm font-medium text-foreground">Net receivable after save: {inr(net)}</div>
            <div className="mt-1">Saving also refreshes % complete and previously billed to today's figures.</div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}